import React, { useEffect, useLayoutEffect } from "react";
import { useIsRestoring, useQueryClient } from "@tanstack/react-query";
import { useAccountQuery } from "../utils/queries";
import { Link, NavLink, Outlet, useParams, useMatch } from "react-router";
import { FiArrowLeft, FiGrid, FiMessageSquare, FiUsers, FiVideo, FiLoader, FiSettings } from "react-icons/fi";
import { useCourse } from "../context/CourseContext";
import AccountMenu from "./AccountMenu";

const Header = () => {
  const { slug } = useParams();
  const liveClass = useMatch("/courses/:slug/classes/:classId/live");
  const { course, setCourse } = useCourse();
  const client = useQueryClient();
  const restoring = useIsRestoring();
  const result = useAccountQuery(["group", slug], `/getaworkspace/${slug}`, { refetchOnMount: "always" });
  useLayoutEffect(() => { if (result.data?.workspace) setCourse(result.data.workspace); }, [result.data, setCourse]);
  const denied = [401, 403, 404].includes(result.error?.status);
  const accountId = result.queryKey[1];
  useEffect(() => {
    if (!denied) return;
    setCourse(null);
    // Keep the active error visible, but discard content that may no longer be accessible.
    client.removeQueries({ predicate: query => query.queryKey[0] === "account"
      && query.queryKey[1] === accountId
      && !(query.queryKey[2] === "group" && query.queryKey[3] === slug) });
  }, [denied, client, accountId, slug, setCourse]);
  const loading = !result.data && !result.error || Boolean(result.data && course?._id !== result.data.workspace?._id);
  const error = result.error && !result.data ? result.error.message : "";
  const tabs = [
    { label: "Feed", path: "", icon: FiGrid },
    { label: "Classes", path: "/classes", icon: FiVideo },
    { label: "Members", path: "/learners", icon: FiUsers },
    { label: "Messages", path: "/messages", icon: FiMessageSquare },
    { label: "Settings", path: "/settings", icon: FiSettings },
  ];
  return (
    <div className="min-h-screen bg-white text-gray-950">
      {!liveClass && <header className="sticky top-0 z-30 border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <div className="flex min-h-16 min-w-0 items-center gap-3 py-3">
            <Link to="/courses" aria-label="All groups" title="All groups" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"><FiArrowLeft className="h-5 w-5" /></Link>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md bg-orange-50 text-base text-orange-800" aria-hidden="true">{!loading && !error && course?.thumbnail ? <img src={course.thumbnail} alt="" className="h-full w-full object-cover" /> : <FiUsers />}</span>
            <div className="min-w-0 flex-1">
              <h1 className="truncate text-base font-semibold" title={course?.name}>{loading ? "Loading group..." : error ? "Group unavailable" : course?.name}</h1>
              {!loading && !error && <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
                <span className="capitalize">{course?.category || "Community"}</span><span className="flex items-center gap-1.5"><FiUsers aria-hidden="true" />{course?.members?.length || 0} members</span>
              </div>}
            </div>
            <AccountMenu />
          </div>
          <nav aria-label="Group navigation" className="grid grid-cols-5 gap-1 sm:flex sm:gap-4">
            {tabs.map(({ label, path, icon: Icon }) => <NavLink key={label} to={`/courses/${slug}${path}`} end={!path} aria-label={label}
              className={({ isActive }) => `flex min-h-12 min-w-0 flex-col items-center justify-center gap-1 border-b-2 px-1 py-2 text-xs font-semibold sm:flex-row sm:gap-2 sm:px-3 sm:text-sm ${isActive ? "border-orange-700 text-orange-800" : "border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900"}`}>
              <Icon aria-hidden="true" className="h-4 w-4 shrink-0" /><span>{label}</span>
            </NavLink>)}
          </nav>
        </div>
      </header>}
      {restoring ? null : loading ? <div role="status" aria-label="Loading group" className="flex min-h-64 items-center justify-center"><FiLoader className="h-6 w-6 animate-spin text-orange-700" /></div>
        : error ? <div className="mx-auto max-w-7xl px-4 py-10"><p role="alert" className="text-sm text-red-700">{error}</p><button onClick={() => result.refetch()} className="mt-4 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm">Try again</button></div>
          : <Outlet />}
    </div>
  );
};
export default Header;
